import { useEffect, useState } from 'react';

/**
 * Suit une media query, et se met à jour quand elle bascule.
 *
 * ── Pourquoi en JavaScript plutôt qu'en classes ─────────────────────────────
 * Les variantes `md:` et `lg:` suffisent tant qu'un état ne dépend QUE de la
 * largeur. La barre latérale, elle, croise deux choses : la largeur de
 * l'écran et un choix de l'utilisateur — replier le menu. Exprimer ce
 * croisement en classes demanderait de dupliquer chaque règle en trois
 * variantes, et de les tenir accordées à la main.
 *
 * Le calculer une fois en JavaScript rend les trois états lisibles, et les
 * rend éprouvables : un test peut poser la largeur et vérifier ce qui s'affiche.
 *
 * `matchMedia` peut manquer dans un environnement de test : son absence rend
 * `false`, ce qui correspond à la mise en page la plus étroite — celle qui
 * fonctionne partout.
 */
export function useMediaQuery(requete: string): boolean {
  const [correspond, setCorrespond] = useState(() => {
    if (typeof globalThis.matchMedia !== 'function') return false;
    return globalThis.matchMedia(requete).matches;
  });

  useEffect(() => {
    if (typeof globalThis.matchMedia !== 'function') return;
    const liste = globalThis.matchMedia(requete);
    const suivre = () => setCorrespond(liste.matches);
    suivre();
    liste.addEventListener('change', suivre);
    return () => liste.removeEventListener('change', suivre);
  }, [requete]);

  return correspond;
}

/** Points de rupture, alignés sur ceux de Tailwind. */
export const RUPTURES = {
  /** À partir de 768 px : tablette. La barre latérale y tient en rail. */
  tablette: '(min-width: 768px)',
  /** À partir de 1024 px : bureau. La barre latérale s'y déplie. */
  bureau: '(min-width: 1024px)',
} as const;
